import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StoresManager } from "../StoresManager";

const formatDate = (iso) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const EditUserDialog = ({ state, onChange, onClose, onSave }) => {
  const set = (patch) => onChange({ ...state, ...patch });

  return (
    <Dialog open={state.open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-mono">User Profile: {state.user?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {/* Registration Info */}
          <div className="space-y-3">
            <h3 className="font-mono text-sm font-bold text-primary border-b border-border/30 pb-1">
              Registration Info
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="font-mono text-xs">Owner&apos;s Name</Label>
                <Input
                  value={state.name}
                  onChange={(e) => set({ name: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">Email</Label>
                <Input
                  type="email"
                  value={state.email}
                  onChange={(e) => set({ email: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-user-email"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">Store Name</Label>
                <Input
                  value={state.storeName}
                  onChange={(e) => set({ storeName: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-store-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">Store Address</Label>
                <Input
                  value={state.storeAddress}
                  onChange={(e) => set({ storeAddress: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-store-address"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="font-mono text-xs">New Password (leave blank to keep current)</Label>
              <Input
                type="text"
                placeholder="Enter new password"
                value={state.password}
                onChange={(e) => set({ password: e.target.value })}
                className="font-mono text-sm"
                data-testid="edit-user-password"
              />
            </div>
            {state.user && (
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground font-mono">
                <span>Joined: {formatDate(state.user.created_at)}</span>
                <span>Last Login: {formatDate(state.user.last_login)}</span>
                <span>Entries: {state.user.entry_count || 0}</span>
              </div>
            )}
          </div>

          {/* Stores Manager (multi-store support) */}
          {state.user?.id && (
            <div className="space-y-3 border-t border-border/30 pt-4">
              <StoresManager userId={state.user.id} userEmail={state.user.email} />
            </div>
          )}

          {/* Subscription Info */}
          <div className="space-y-3">
            <h3 className="font-mono text-sm font-bold text-primary border-b border-border/30 pb-1">
              Subscription Info
            </h3>            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="font-mono text-xs">Plan</Label>
                <select
                  value={state.subscriptionPlan}
                  onChange={(e) => set({ subscriptionPlan: e.target.value })}
                  className="w-full h-9 rounded-xl border border-border bg-background px-3 text-sm font-mono"
                  data-testid="edit-sub-plan"
                >
                  <option value="">No Plan</option>
                  <option value="trial">Trial</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={state.subscriptionStart}
                  onChange={(e) => set({ subscriptionStart: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-sub-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">End Date</Label>
                <Input
                  type="date"
                  value={state.subscriptionEnd}
                  onChange={(e) => set({ subscriptionEnd: e.target.value })}
                  className="font-mono text-sm"
                  data-testid="edit-sub-end"
                />
              </div>
              <div className="space-y-1">
                <Label className="font-mono text-xs">Notes</Label>
                <Input
                  value={state.subscriptionNotes}
                  onChange={(e) => set({ subscriptionNotes: e.target.value })}
                  placeholder="General notes"
                  className="font-mono text-sm"
                  data-testid="edit-sub-notes"
                />
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="space-y-3">
            <h3 className="font-mono text-sm font-bold text-primary border-b border-border/30 pb-1">
              Payment Info
            </h3>
            <div className="space-y-2">
              <Label className="font-mono text-xs">Payment Method</Label>
              <select
                value={state.paymentMethod}
                onChange={(e) => set({ paymentMethod: e.target.value })}
                className="w-full h-9 rounded-xl border border-border bg-background px-3 text-sm font-mono"
                data-testid="edit-payment-method"
              >
                <option value="">None</option>
                <option value="check">Check</option>
                <option value="zelle">Zelle</option>
              </select>
            </div>
            {state.paymentMethod === 'check' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="font-mono text-xs">Check Number</Label>
                  <Input
                    value={state.checkNumber}
                    onChange={(e) => set({ checkNumber: e.target.value })}
                    placeholder="#"
                    className="font-mono text-sm"
                    data-testid="edit-check-number"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="font-mono text-xs">Amount</Label>
                  <Input
                    value={state.paymentAmount}
                    onChange={(e) => set({ paymentAmount: e.target.value })}
                    placeholder="$0.00"
                    className="font-mono text-sm"
                    data-testid="edit-payment-amount"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="font-mono text-xs">Date</Label>
                  <Input
                    type="date"
                    value={state.paymentDate}
                    onChange={(e) => set({ paymentDate: e.target.value })}
                    className="font-mono text-sm"
                    data-testid="edit-payment-date"
                  />
                </div>
              </div>
            )}
            {state.paymentMethod === 'zelle' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="font-mono text-xs">Amount</Label>
                  <Input
                    value={state.paymentAmount}
                    onChange={(e) => set({ paymentAmount: e.target.value })}
                    placeholder="$0.00"
                    className="font-mono text-sm"
                    data-testid="edit-zelle-amount"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="font-mono text-xs">Date</Label>
                  <Input
                    type="date"
                    value={state.paymentDate}
                    onChange={(e) => set({ paymentDate: e.target.value })}
                    className="font-mono text-sm"
                    data-testid="edit-zelle-date"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button className="btn-primary" onClick={onSave} data-testid="save-edit-user">
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
